import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  UserCheck, Calendar, DollarSign, Clock, CheckCircle,
  Loader2, AlertCircle, MapPin, Users, Award, Star
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Assignment Card
const AssignmentCard = ({ assignment, onConfirm, confirming }) => {
  const statusColors = {
    assigned: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    confirmed: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    completed: 'bg-green-500/10 text-green-600 border-green-500/20',
    cancelled: 'bg-red-500/10 text-red-600 border-red-500/20'
  };

  return (
    <Card className="hover:border-primary/50 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <Badge className={statusColors[assignment.status] || ''}>
            {assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1)}
          </Badge>
          {assignment.paid && (
            <Badge variant="outline" className="text-green-600">
              <DollarSign className="h-3 w-3 mr-1" />
              Paid
            </Badge>
          )}
        </div>
        
        {assignment.game && (
          <div className="space-y-2">
            <h3 className="font-semibold">
              {assignment.game.team1_name} vs {assignment.game.team2_name}
            </h3>
            <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {assignment.game.scheduled_time ? new Date(assignment.game.scheduled_time).toLocaleDateString() : 'TBD'}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {assignment.game.field || 'TBD'}
              </span>
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="font-mono text-lg font-bold text-primary">
                ${assignment.pay_rate?.toFixed(2)}
              </span>
              {assignment.status === 'assigned' && (
                <Button 
                  size="sm" 
                  onClick={() => onConfirm(assignment.id)}
                  disabled={confirming}
                  className="gap-1"
                >
                  {confirming ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                  Confirm
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Register Dialog
const RegisterDialog = ({ open, onOpenChange, onRegister }) => {
  const [form, setForm] = useState({
    certifications: [],
    experience_years: 0,
    pay_rate: 25,
    bio: '',
    phone: ''
  });
  const [newCert, setNewCert] = useState('');
  const [registering, setRegistering] = useState(false);

  const addCertification = () => {
    if (newCert.trim()) {
      setForm(prev => ({
        ...prev,
        certifications: [...prev.certifications, newCert.trim()]
      }));
      setNewCert('');
    }
  };

  const handleRegister = async () => {
    setRegistering(true);
    try {
      await onRegister(form);
      onOpenChange(false);
    } finally {
      setRegistering(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            Register as Referee
          </DialogTitle>
          <DialogDescription>
            Create your referee profile to start receiving game assignments
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label>Certifications</Label>
            <div className="flex gap-2 mt-2">
              <Input
                value={newCert}
                onChange={(e) => setNewCert(e.target.value)}
                placeholder="e.g., NFL Flag Certified"
                onKeyPress={(e) => e.key === 'Enter' && addCertification()}
              />
              <Button type="button" onClick={addCertification}>Add</Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {form.certifications.map((cert, i) => (
                <Badge key={i} variant="secondary" className="gap-1">
                  {cert}
                  <button onClick={() => setForm({
                    ...form, 
                    certifications: form.certifications.filter((_, j) => j !== i)
                  })}>×</button>
                </Badge>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Years of Experience</Label>
              <Input
                type="number"
                min="0"
                value={form.experience_years}
                onChange={(e) => setForm({...form, experience_years: parseInt(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>Pay Rate (per game)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2">$</span>
                <Input
                  type="number"
                  min="0"
                  step="5"
                  value={form.pay_rate}
                  onChange={(e) => setForm({...form, pay_rate: parseFloat(e.target.value) || 25})}
                  className="pl-7"
                />
              </div>
            </div>
          </div>
          
          <div>
            <Label>Phone Number</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({...form, phone: e.target.value})}
              placeholder="+1 (555) 123-4567"
            />
          </div>
          
          <div>
            <Label>Bio</Label>
            <Textarea
              value={form.bio}
              onChange={(e) => setForm({...form, bio: e.target.value})}
              placeholder="Tell organizers about your experience..."
              rows={3}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleRegister} disabled={registering} className="gap-2">
            {registering && <Loader2 className="h-4 w-4 animate-spin" />}
            Register
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RefereeDashboard = () => {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Try to get referee profile
      const profileRes = await axios.get(`${API}/referee/profile`).catch(() => null);
      if (profileRes?.data) {
        setProfile(profileRes.data);
        
        // Get assignments and earnings
        const [assignRes, earnRes] = await Promise.all([
          axios.get(`${API}/referee/assignments`),
          axios.get(`${API}/referee/earnings`)
        ]);
        setAssignments(assignRes.data);
        setEarnings(earnRes.data);
      }
    } catch (error) {
      console.error('Error fetching referee data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (profileData) => {
    try {
      await axios.post(`${API}/referee/register`, profileData);
      toast.success('Registered as referee!');
      await refreshUser();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      throw error;
    }
  };

  const handleConfirm = async (assignmentId) => {
    setConfirming(true);
    try {
      await axios.put(`${API}/referee/assignments/${assignmentId}/confirm`);
      toast.success('Assignment confirmed');
      fetchData();
    } catch (error) {
      toast.error('Failed to confirm assignment');
    } finally {
      setConfirming(false);
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-muted-foreground">Please login to access the referee dashboard</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not registered as referee
  if (!profile) {
    return (
      <div className="container mx-auto px-4 py-8" data-testid="referee-register-page">
        <Card className="max-w-lg mx-auto">
          <CardContent className="py-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <UserCheck className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Become a Referee</h2>
            <p className="text-muted-foreground mb-6">
              Register as an official to receive game assignments and earn money
            </p>
            <Button onClick={() => setRegisterOpen(true)} size="lg" className="gap-2">
              <UserCheck className="h-5 w-5" />
              Register as Referee
            </Button>
          </CardContent>
        </Card>
        
        <RegisterDialog
          open={registerOpen}
          onOpenChange={setRegisterOpen}
          onRegister={handleRegister}
        />
      </div>
    );
  }

  // Referee dashboard
  const upcomingAssignments = assignments.filter(a => ['assigned', 'confirmed'].includes(a.status));
  const completedAssignments = assignments.filter(a => a.status === 'completed');

  return (
    <div className="container mx-auto px-4 py-8" data-testid="referee-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <UserCheck className="h-8 w-8" />
          Referee Dashboard
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage your assignments and track earnings
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{upcomingAssignments.length}</p>
                <p className="text-sm text-muted-foreground">Upcoming Games</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{earnings?.total_games || 0}</p>
                <p className="text-sm text-muted-foreground">Games Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold">${earnings?.total_earned?.toFixed(2) || '0.00'}</p>
                <p className="text-sm text-muted-foreground">Total Earned</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">${earnings?.total_pending?.toFixed(2) || '0.00'}</p>
                <p className="text-sm text-muted-foreground">Pending Payment</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Profile Summary */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Your Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-6">
            <div>
              <p className="text-sm text-muted-foreground">Pay Rate</p>
              <p className="text-xl font-bold">${profile.pay_rate}/game</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Experience</p>
              <p className="text-xl font-bold">{profile.experience_years} years</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Certifications</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {profile.certifications?.length > 0 ? (
                  profile.certifications.map((cert, i) => (
                    <Badge key={i} variant="secondary">{cert}</Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground">None listed</span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Assignments Tabs */}
      <Tabs defaultValue="upcoming">
        <TabsList className="mb-4">
          <TabsTrigger value="upcoming" className="gap-1">
            <Calendar className="h-4 w-4" />
            Upcoming ({upcomingAssignments.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="gap-1">
            <CheckCircle className="h-4 w-4" />
            Completed ({completedAssignments.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="upcoming">
          {upcomingAssignments.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Upcoming Assignments</h3>
                <p className="text-muted-foreground">
                  You will receive notifications when organizers assign you to games
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingAssignments.map(assignment => (
                <AssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  onConfirm={handleConfirm}
                  confirming={confirming}
                />
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="completed">
          {completedAssignments.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Completed Games Yet</h3>
                <p className="text-muted-foreground">
                  Your completed assignments will appear here
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {completedAssignments.map(assignment => (
                <AssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  onConfirm={handleConfirm}
                  confirming={confirming}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
